import { PrWorkingPatternTypeCode } from "@odata/GeneratedEnums";
import { isNotDefined } from "@utils/general";
import { Dayjs } from "dayjs";
import React, { useCallback, useMemo } from "react";

import TestIds from "../../testIds";
import DateType from "../../types/Date";
import { formatDateToDateString, getDaysInterval, getNextMonday, getWeekdays } from "../inputs/date/utils";
import DayInput from "../inputs/dayInput/DayInput";
import { CalendarGrid, DayLabel } from "./CalendarWithInputs.styles";

export interface IProps {
    startDate?: Dayjs;
    onChange: (val: number, day: Dayjs) => void;
    onSelect?: (day: Dayjs) => void;
    selectedDay?: Date;
    type: PrWorkingPatternTypeCode;
    rotationLength?: number;
    isDifferentOddAndEvenWeek?: boolean;
    daysValuesMap?: Record<string, number>;
    daysWithError?: string[];
    isReadOnly?: boolean;
}

const CalendarWithInputs: React.FC<IProps> = ({
                                                  startDate,
                                                  onChange,
                                                  onSelect,
                                                  selectedDay,
                                                  type,
                                                  rotationLength,
                                                  isDifferentOddAndEvenWeek,
                                                  daysValuesMap,
                                                  daysWithError,
                                                  isReadOnly
                                              }) => {
    const isRotation = type === PrWorkingPatternTypeCode.Rotation;

    const handleChange = useCallback((val: number, date: Dayjs) => {
        if (val < 24) {
            onChange(val, date);
        }
    }, [onChange]);

    const intervalLength = isRotation ? rotationLength ?? 0 : isDifferentOddAndEvenWeek ? 14 : 7;
    const colsCount = Math.min(intervalLength, 7);

    const isInvalid = isRotation && (!DateType.isValid(startDate) || isNotDefined(intervalLength));

    const getDays = useMemo(() => {
        if (isInvalid) {
            return [];
        }
        return getDaysInterval(isRotation ? startDate : getNextMonday(startDate, isDifferentOddAndEvenWeek), intervalLength);
    }, [startDate, isDifferentOddAndEvenWeek, intervalLength, isRotation, isInvalid]);

    if (isInvalid) {
        return null;
    }

    let weekdayLabels = getWeekdays();
    const days = getDays;

    if (isRotation) {
        const offset = (startDate.day() + 6) % 7;
        weekdayLabels = weekdayLabels.slice(offset, 7).concat(weekdayLabels.slice(0, offset)).slice(0, colsCount);
    }
    
    const selectedDayId = selectedDay ? formatDateToDateString(selectedDay) : null;

    return <CalendarGrid $rowLength={colsCount} data-testid={TestIds.CalendarGrid}>
        {weekdayLabels.map((day) => <DayLabel key={day} data-testid={TestIds.DayLabel}>{day}</DayLabel>)}
        {days.map((day) => {
            const id = formatDateToDateString(day);
            return <DayInput
                key={id}
                hasError={daysWithError?.includes(id)}
                value={daysValuesMap[id]}
                onChange={handleChange}
                onDaySelect={onSelect}
                isSelected={id === selectedDayId}
                isReadOnly={isReadOnly}
                date={day}
            />;
        })}
    </CalendarGrid>;
};

export default CalendarWithInputs;